import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal, MaskLines, ImageReveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { BLOG_POSTS, getPost } from "../blogs.js";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useSeo({
    title: post ? `${post.title} | The Oneiros Journal` : "The Oneiros Journal | Oneiros Sports Club Surat",
    description: post?.excerpt || "Stories from Oneiros Sports Club Surat.",
    image: post?.image,
  });

  if (!post) return <Navigate to="/blog" replace />;

  const idx = BLOG_POSTS.indexOf(post);
  const next = BLOG_POSTS[(idx + 1) % BLOG_POSTS.length];

  return (
    <article data-testid="blog-post-page" className="bg-white">
      <section className="mx-auto max-w-3xl px-6 pb-10 pt-36 lg:pt-48">
        <Reveal>
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-mute transition-colors hover:text-brand"
          >
            <ArrowLeft className="h-4 w-4" /> All Articles
          </Link>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em]">
            <span className="text-brand">{post.category}</span>
            <span className="text-mute/60">{post.date}</span>
            <span className="text-mute/60">{post.readTime}</span>
          </div>
        </Reveal>
        <MaskLines lines={[post.title]} className="mt-4 font-display text-3xl leading-[1.12] text-navy sm:text-4xl lg:text-5xl" />
        <Reveal delay={0.15}>
          <p className="mt-5 text-base leading-relaxed text-mute md:text-lg">{post.excerpt}</p>
        </Reveal>
      </section>

      <Reveal className="mx-auto max-w-4xl px-6">
        <ImageReveal src={post.image} alt={post.title} className="aspect-[16/9] w-full" />
      </Reveal>

      <div className="mx-auto max-w-3xl px-6 py-14">
        {post.content.map((block, i) =>
          block.h ? (
            <Reveal key={i}>
              <h2 className="mt-10 font-display text-2xl leading-snug text-navy sm:text-3xl">{block.h}</h2>
            </Reveal>
          ) : (
            <Reveal key={i} delay={0.05}>
              <p className="mt-5 text-base leading-[1.85] text-mute">{block.p}</p>
            </Reveal>
          )
        )}
      </div>

      <section className="border-t border-hairline bg-mist py-16">
        <div className="mx-auto flex max-w-3xl flex-col items-start gap-8 px-6 sm:flex-row sm:items-center sm:justify-between">
          <Link to={`/blog/${next.slug}`} data-testid="blog-next-link" className="group max-w-xs">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand">Next Article</p>
            <p className="mt-2 font-display text-xl leading-snug text-navy transition-colors group-hover:text-brand">{next.title}</p>
          </Link>
          <Link
            to="/contact"
            data-testid="blog-enquire-cta"
            className="inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-dark"
          >
            Visit Oneiros
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </article>
  );
}
